import React, { useEffect, useRef, useState } from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SectionHeading from '../../components/SectionHeading';
import ScreenFrame from '../../components/ScreenFrame';
import { colors } from '../../theme';
import { ease } from '../../utils/animations';
import { track } from '../../utils/analytics';
import { listedWork as work } from '../../data/work';

/**
 * A showcase, not a grid: the three projects are a list on the left, and
 * the one under the pointer (or focus) fills a large frame on the right.
 * One viewport, one big legible screenshot at a time, and the list makes
 * "three things, each a page" obvious. Hover previews; click goes to the
 * project page. Below md it falls back to stacked cards, since there is
 * nothing to hover.
 */

const HOVER_INTENT_MS = 90;

// Leave modified clicks (new tab, new window) to the browser.
const isPlainClick = (e) =>
    e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && !e.defaultPrevented;

const Showcase = ({ onOpen }) => {
    const [active, setActive] = useState(0);
    const intent = useRef(null);
    const current = work[active];

    const select = (i) => {
        clearTimeout(intent.current);
        intent.current = setTimeout(() => setActive(i), HOVER_INTENT_MS);
    };
    useEffect(() => () => clearTimeout(intent.current), []);

    return (
        <Grid container spacing={{ md: 6, lg: 8 }} alignItems="stretch">
            {/* List */}
            <Grid size={{ md: 5 }} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, borderTop: '1px solid #e8edeb' }} onMouseLeave={() => clearTimeout(intent.current)}>
                    {work.map((project, i) => {
                        const isActive = i === active;
                        return (
                            <Box component="li" key={project.id} sx={{ borderBottom: '1px solid #e8edeb' }}>
                                <Box
                                    component="a"
                                    href={`/work/${project.id}`}
                                    onClick={(e) => { if (!isPlainClick(e)) return; e.preventDefault(); onOpen(project); }}
                                    onMouseEnter={() => select(i)}
                                    onFocus={() => { clearTimeout(intent.current); setActive(i); }}
                                    aria-current={isActive ? 'true' : undefined}
                                    sx={{
                                        display: 'grid', gridTemplateColumns: '44px 1fr auto', alignItems: 'start',
                                        columnGap: 2, py: { md: 2.5, lg: 2.75 }, pr: 1,
                                        textDecoration: 'none', color: 'inherit',
                                        position: 'relative',
                                        '&::before': {
                                            content: '""', position: 'absolute', left: -1, top: 0, bottom: 0,
                                            width: 3, backgroundColor: colors.green,
                                            transform: isActive ? 'scaleY(1)' : 'scaleY(0)',
                                            transformOrigin: 'top', transition: 'transform 0.3s ease',
                                        },
                                        '&:focus-visible': { outline: 'none' },
                                        '&:focus-visible .show-name': { textDecoration: 'underline', textUnderlineOffset: 6 },
                                    }}
                                >
                                    <Typography sx={{
                                        pt: '6px', fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums', letterSpacing: '0.06em',
                                        color: isActive ? colors.greenText : 'rgba(0,0,0,0.45)', transition: 'color 0.3s ease', pl: 2.5,
                                    }}>
                                        {String(i + 1).padStart(2, '0')}
                                    </Typography>
                                    <Box>
                                        <Typography
                                            variant="overline"
                                            sx={{ display: 'block', color: colors.greenText, letterSpacing: '0.16em', fontSize: '0.66rem', fontWeight: 600, lineHeight: 1.6, mb: 0.5,
                                                  opacity: isActive ? 1 : 0.7, transition: 'opacity 0.3s ease' }}
                                        >
                                            {project.eyebrow}
                                        </Typography>
                                        <Typography
                                            className="show-name"
                                            component="h3"
                                            sx={{
                                                fontWeight: 300, fontSize: 'clamp(1.5rem, 2vw, 1.85rem)', letterSpacing: '-0.015em', lineHeight: 1.1,
                                                color: isActive ? '#111' : 'rgba(0,0,0,0.58)', transition: 'color 0.3s ease', mb: 1,
                                            }}
                                        >
                                            {project.name}
                                        </Typography>
                                        <Box sx={{
                                            display: 'grid', gridTemplateRows: isActive ? '1fr' : '0fr',
                                            transition: 'grid-template-rows 0.35s ease',
                                        }}>
                                            <Box sx={{ overflow: 'hidden' }}>
                                                <Typography sx={{ color: 'text.secondary', fontSize: '0.98rem', lineHeight: 1.65, maxWidth: 380, pb: 0.5 }}>
                                                    {project.tagline}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{
                                        pt: '10px', color: isActive ? '#111' : 'rgba(0,0,0,0.35)', transition: 'color 0.3s ease, transform 0.3s ease',
                                        transform: isActive ? 'translateX(0)' : 'translateX(-4px)',
                                    }}>
                                        <ArrowRight size={20} strokeWidth={1.75} />
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Grid>

            {/* Stage: all three frames stay mounted; the active one fades up. */}
            <Grid size={{ md: 7 }} sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                    component="a"
                    href={`/work/${current.id}`}
                    onClick={(e) => { if (!isPlainClick(e)) return; e.preventDefault(); onOpen(current); }}
                    aria-label={`View ${current.name}`}
                    sx={{
                        display: 'block', width: '100%', position: 'relative', aspectRatio: '16 / 10',
                        '&:hover .show-frame.is-active': {
                            transform: 'translateY(-3px)',
                            boxShadow: '0 1px 2px rgba(16,24,20,0.06), 0 28px 60px rgba(16,24,20,0.16)',
                        },
                        '&:focus-visible': { outline: 'none' },
                        '&:focus-visible .show-frame.is-active': { boxShadow: `0 0 0 3px #fff, 0 0 0 6px ${colors.green}` },
                    }}
                >
                    {work.map((project, i) => {
                        const isActive = i === active;
                        return (
                            <ScreenFrame
                                key={project.id}
                                className={`show-frame${isActive ? ' is-active' : ''}`}
                                screen={project.screens[0]}
                                aspect="16 / 10"
                                sx={{
                                    position: 'absolute', inset: 0,
                                    opacity: isActive ? 1 : 0,
                                    transform: isActive ? 'translateY(0)' : 'translateY(10px)',
                                    transition: 'opacity 0.4s ease, transform 0.5s cubic-bezier(0.22,1,0.36,1), box-shadow 0.4s ease',
                                    pointerEvents: isActive ? 'auto' : 'none',
                                    boxShadow: '0 1px 2px rgba(16,24,20,0.06), 0 20px 48px rgba(16,24,20,0.12)',
                                }}
                            />
                        );
                    })}
                </Box>
            </Grid>
        </Grid>
    );
};

// Below md: nothing to hover, so each project is its own card.
const StackedCards = ({ onOpen }) => (
    <Box sx={{ display: 'grid', gap: 5 }}>
        {work.map((project, i) => (
            <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.55, delay: 0.05, ease }}
            >
                <Box
                    component="a"
                    href={`/work/${project.id}`}
                    onClick={(e) => { if (!isPlainClick(e)) return; e.preventDefault(); onOpen(project); }}
                    sx={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
                >
                    <Typography variant="overline" sx={{ display: 'block', color: colors.greenText, letterSpacing: '0.16em', fontSize: '0.66rem', fontWeight: 600, lineHeight: 1.6, mb: 0.5 }}>
                        {project.eyebrow}
                    </Typography>
                    <Typography component="h3" sx={{ fontWeight: 300, fontSize: '1.7rem', letterSpacing: '-0.015em', lineHeight: 1.1, mb: 2, color: '#111' }}>
                        {project.name}
                    </Typography>
                    <ScreenFrame screen={project.screens[0]} aspect="16 / 10" compact sx={{ mb: 2 }} />
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.98rem', lineHeight: 1.65, mb: 1.5 }}>
                        {project.tagline}
                    </Typography>
                    <Typography sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontWeight: 600, fontSize: '0.92rem', color: '#111' }}>
                        View project <ArrowRight size={16} />
                    </Typography>
                </Box>
            </motion.div>
        ))}
    </Box>
);

const WorkSection = () => {
    const navigate = useNavigate();
    const open = (project) => {
        track('work_open', { project: project.id });
        // Mark where we left from, so Back lands on this section, not the hero.
        navigate('/#work', { replace: true });
        navigate(`/work/${project.id}`);
    };

    return (
        <Box
            id="work"
            component="section"
            sx={{ py: { xs: 9, md: 13 }, scrollMarginTop: { xs: '56px', md: '64px' } }}
        >
            <Container maxWidth="lg">
                <SectionHeading eyebrow="Work" title="Some of what we've built." sx={{ mb: { xs: 5, md: 7 } }} />
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Showcase onOpen={open} />
                </Box>
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    <StackedCards onOpen={open} />
                </Box>
            </Container>
        </Box>
    );
};

export default WorkSection;
